import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HIGHLIGHT_CLOSE, HIGHLIGHT_OPEN, searchProducts, type ProductHit } from '../api/products';
import { ErrorBanner } from './ErrorBanner';

export interface ProductSelection {
  rouCode: string;
  countryCode: string;
  productName: string;
  kiteProductNumbers: string[];
}

export interface ProductPickerProps {
  selection: ProductSelection | null;
  /** Off where the surrounding layout already labels the field, as the detail page's list does. */
  showLabel?: boolean;
  countryCode: string | null;
  onChange: (selection: ProductSelection | null) => void;
  disabled?: boolean;
  search?: (query: string, countryCode: string | null, signal?: AbortSignal) => Promise<ProductHit[]>;
}

/** Splits on the sentinels the search put around each match, so nothing is rendered as HTML. */
function Highlighted({ text }: { text: string }) {
    return (
        <>
            {text.split(HIGHLIGHT_OPEN).map((chunk, index) => {
                if (index === 0) {
                    return <span key={index}>{chunk}</span>;
                }
                const [matched, rest = ''] = chunk.split(HIGHLIGHT_CLOSE);
                return (
                    <span key={index}>
                        <mark className="product-hit__match">{matched}</mark>
                        {rest}
                    </span>
                );
            })}
        </>
    );
}

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

export function ProductPicker({
  selection,
  countryCode,
  onChange,
  disabled = false,
  showLabel = true,
  search = searchProducts,
}: ProductPickerProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<ProductHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const generation = useRef(0);

  useEffect(() => {
    const trimmed = term.trim();
    if (selection !== null || trimmed.length < MIN_QUERY_LENGTH) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    // A slow earlier query must never overwrite the answer to a later one.
    const current = ++generation.current;

    const timer = window.setTimeout(() => {
      setSearching(true);
      search(trimmed, countryCode, controller.signal)
        .then((hits) => {
          if (current !== generation.current) {
            return;
          }
          setResults(hits);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted || current !== generation.current) {
            return;
          }
          setResults([]);
          setError(caught);
        })
        .finally(() => {
          if (current === generation.current) {
            setSearching(false);
          }
        });
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [term, selection, countryCode, search]);

  function select(hit: ProductHit) {
    onChange({
      rouCode: hit.rouCode,
      countryCode: hit.countryCode,
      productName: hit.productName,
      kiteProductNumbers: hit.kiteProductNumbers,
    });
    setResults([]);
    setTerm('');
  }

  if (selection !== null) {
    return (
      <div className="field">
        {showLabel && (
          <span className="form-label">
            <i className="pi pi-box me-1" aria-hidden="true" />
            {t('productPicker.pickedProduct')}
          </span>
        )}
        <div className="picked-user">
          <i className="pi pi-check-circle" aria-hidden="true" />
          <span>
            {selection.productName}{' '}
            <span className="mono">
              ({selection.rouCode} &middot; {selection.countryCode})
            </span>
          </span>
          <button
            type="button"
            className="btn btn-link btn-sm"
            onClick={() => onChange(null)}
            disabled={disabled}
          >
            <i className="pi pi-refresh me-1" aria-hidden="true" />
            {t('productPicker.change')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="field">
      {showLabel && (
        <label htmlFor="product-search" className="is-required">
          <i className="pi pi-search me-1" aria-hidden="true" />
          {t('productPicker.searchLabel')}
        </label>
      )}
      <input
        id="product-search"
        type="search"
        className="form-control"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder={t('productPicker.placeholder')}
        disabled={disabled}
        autoComplete="off"
      />
      {searching && <p className="form-text">{t('productPicker.searching')}</p>}
      {error !== null && <ErrorBanner error={error} />}
      {results.length > 0 && (
        <ul className="list-group mt-2">
          {results.map((hit) => (
            <li key={hit.objectID} className="list-group-item list-group-item-action p-0">
              <button
                type="button"
                className="btn btn-link text-start w-100 p-2"
                onClick={() => select(hit)}
                disabled={disabled}
              >
                <span className="d-block fw-semibold">
                  <Highlighted text={hit.highlighted.productName} />
                </span>
                <span className="d-block small text-body-secondary">
                  <Highlighted text={hit.highlighted.rouCode} />
                  {hit.highlighted.kiteProductNumbers.length > 0 && (
                    <>
                      {' '}
                      &middot;{' '}
                      {hit.highlighted.kiteProductNumbers.map((number, index) => (
                        <span key={index}>
                          {index > 0 && ', '}
                          <Highlighted text={number} />
                        </span>
                      ))}
                    </>
                  )}
                  {' '}&middot; {hit.countryName} &middot;{' '}
                  {t('productPicker.datasheetCount', { count: hit.safetyDatasheetCount })}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!searching && results.length === 0 && term.trim().length >= MIN_QUERY_LENGTH && error === null && (
        <p className="form-text">{t('productPicker.noResults')}</p>
      )}
    </div>
  );
}
